/**
 * Channel intake — `/channels/orders`.
 *
 * What arrived from a channel, what became of it, and what to do about the
 * ones that failed. Three contract facts shape the screen.
 *
 * Retry is a selection, not a row action: the operation takes a set of ids and
 * answers one disposition per id. Only "enqueued" did anything — the other
 * three mean the order was already pending, already imported, or unknown — so
 * the result is reported per id instead of as a single success toast. Only
 * failed rows can be selected, because those are the only ones a retry helps.
 *
 * The raw payload is the source document, encrypted at rest and purgeable.
 * Reading it is an audited decryption, so it is fetched on an explicit reveal,
 * never cached, and a 410 is reported as "purged" rather than as a failure.
 *
 * An intake record is not an order. When one imported successfully it carries
 * the id of the sales order it became, and the screen links there rather than
 * restating its contents.
 */
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type { ColumnDef } from "@tanstack/react-table";
import { RefreshCw } from "lucide-react";
import {
  Badge,
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  cn,
} from "@erp/ui";

import { DataTable, dateTimeCell, textCell } from "@/components/data-table";
import { ApiError } from "@/lib/api/errors";
import { formatDateTime } from "@/lib/format";
import { newIdempotencyKey } from "@/features/shared/ops-api";

import {
  CHANNEL_ORDER_STATUSES,
  retryChannelOrders,
  type ChannelOrderFilters,
  type ChannelOrderStatus,
  type ChannelOrderSummary,
  type RetryChannelOrderResult,
} from "../api";
import {
  useChannelNames,
  useChannelOrder,
  useChannelOrderRaw,
  useChannelOrderList,
} from "../queries";

const ALL = "__all__";

const STATUS_VARIANTS: Record<ChannelOrderStatus, "outline" | "success" | "destructive"> = {
  pending: "outline",
  imported: "success",
  error: "destructive",
};

export interface ChannelOrdersPageProps {
  canRetry?: boolean;
  canReadRaw?: boolean;
  canReadChannels?: boolean;
  onOpenSalesOrder?: (salesOrderId: string) => void;
  pageLimit?: number;
}

export function ChannelOrdersPage({
  canRetry = false,
  canReadRaw = false,
  canReadChannels = true,
  onOpenSalesOrder,
  pageLimit,
}: ChannelOrdersPageProps) {
  const { t, i18n } = useTranslation();
  const [status, setStatus] = useState<string>(ALL);
  const [channelId, setChannelId] = useState<string>(ALL);
  const [externalId, setExternalId] = useState("");
  const [selected, setSelected] = useState<string[]>([]);
  const [retryResults, setRetryResults] = useState<RetryChannelOrderResult[] | null>(null);
  const [retrying, setRetrying] = useState(false);
  const [retryError, setRetryError] = useState<string | null>(null);
  const [openId, setOpenId] = useState<string | null>(null);
  const [rawOpen, setRawOpen] = useState(false);

  const { channelName, channels } = useChannelNames(canReadChannels);

  const filters = useMemo<ChannelOrderFilters>(() => {
    const built: ChannelOrderFilters = {};
    if (status !== ALL) built.status = status as ChannelOrderStatus;
    if (channelId !== ALL) built.channel_id = channelId;
    if (externalId !== "") built.external_id = externalId;
    return built;
  }, [status, channelId, externalId]);

  const query = useChannelOrderList(filters, pageLimit);
  const detail = useChannelOrder(openId ?? undefined);
  const raw = useChannelOrderRaw(openId ?? "", canReadRaw && rawOpen && openId !== null);
  const rawPurged = raw.error instanceof ApiError && raw.error.status === 410;

  const toggle = (id: string) =>
    setSelected((current) =>
      current.includes(id) ? current.filter((value) => value !== id) : [...current, id],
    );

  const runRetry = async () => {
    if (selected.length === 0) return;
    setRetrying(true);
    setRetryError(null);
    setRetryResults(null);
    try {
      const accepted = await retryChannelOrders(selected, newIdempotencyKey());
      setRetryResults([...accepted.results]);
      setSelected([]);
      await query.refetch();
    } catch {
      setRetryError(t("channels.orders.retryError", "The retry could not be submitted."));
    } finally {
      setRetrying(false);
    }
  };

  const columns = useMemo<ColumnDef<ChannelOrderSummary, unknown>[]>(() => {
    const base: ColumnDef<ChannelOrderSummary, unknown>[] = [
      {
        id: "increment_id",
        accessorKey: "increment_id",
        header: t("channels.orders.columns.reference", "Reference"),
        cell: ({ row }) => (
          <span dir="ltr" className="font-mono text-xs">
            {row.original.increment_id ?? row.original.external_id}
          </span>
        ),
      },
      {
        id: "channel_id",
        accessorKey: "channel_id",
        header: t("channels.orders.columns.channel", "Channel"),
        cell: ({ row }) => textCell(channelName(row.original.channel_id)),
      },
      {
        id: "status",
        accessorKey: "status",
        header: t("channels.orders.columns.status", "Status"),
        cell: ({ row }) => (
          <Badge variant={STATUS_VARIANTS[row.original.status]}>
            {t(`channels.orderStatus.${row.original.status}`, row.original.status)}
          </Badge>
        ),
      },
      {
        id: "error_code",
        accessorKey: "error_code",
        header: t("channels.orders.columns.failure", "Failure"),
        cell: ({ row }) =>
          row.original.error_code === undefined
            ? textCell("—")
            : textCell(
                `${t(`channels.errorStage.${row.original.error_stage ?? "fetch"}`, row.original.error_stage ?? "")} · ${row.original.error_code}`,
              ),
      },
      {
        id: "attempt_count",
        accessorKey: "attempt_count",
        header: t("channels.orders.columns.attempts", "Attempts"),
        meta: { numeric: true },
        cell: ({ row }) => textCell(String(row.original.attempt_count)),
      },
      {
        id: "created_at",
        accessorKey: "created_at",
        header: t("channels.orders.columns.received", "Received"),
        cell: ({ row }) => dateTimeCell(row.original.created_at),
      },
    ];

    if (!canRetry) return base;
    return [
      {
        id: "select",
        header: "",
        cell: ({ row }) => {
          const failed = row.original.status === "error";
          return (
            <Checkbox
              checked={selected.includes(row.original.id)}
              disabled={!failed}
              aria-label={t("channels.orders.selectRow", "Select {{ref}} for retry", {
                ref: row.original.increment_id ?? row.original.external_id,
              })}
              onCheckedChange={() => toggle(row.original.id)}
              onClick={(event) => event.stopPropagation()}
            />
          );
        },
      },
      ...base,
    ];
  }, [t, channelName, canRetry, selected]);

  const openOrder = detail.data?.order;
  const revision = detail.data?.latest_revision_metadata;
  const attempt = detail.data?.latest_attempt;
  const when = (iso: string): string => {
    try {
      return formatDateTime(iso, i18n.language);
    } catch {
      return iso;
    }
  };

  return (
    <div className="flex flex-col gap-4 p-4">
      <div className="space-y-1">
        <h1 className="text-xl font-semibold tracking-tight">
          {t("channels.orders.title", "Channel intake")}
        </h1>
        <p className="text-sm text-muted-foreground">
          {t(
            "channels.orders.subtitle",
            "Orders received from a channel, and what became of each one.",
          )}
        </p>
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <div className="space-y-1.5">
          <Label htmlFor="intake-status">{t("channels.orders.columns.status", "Status")}</Label>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger id="intake-status" className="w-44">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>{t("filters.all", "All")}</SelectItem>
              {CHANNEL_ORDER_STATUSES.map((value) => (
                <SelectItem key={value} value={value}>
                  {t(`channels.orderStatus.${value}`, value)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {canReadChannels && channels.length > 0 ? (
          <div className="space-y-1.5">
            <Label htmlFor="intake-channel">
              {t("channels.orders.columns.channel", "Channel")}
            </Label>
            <Select value={channelId} onValueChange={setChannelId}>
              <SelectTrigger id="intake-channel" className="w-56">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>{t("filters.all", "All")}</SelectItem>
                {channels.map((channel) => (
                  <SelectItem key={channel.id} value={channel.id}>
                    {channel.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        ) : null}

        <div className="space-y-1.5">
          <Label htmlFor="intake-external">
            {t("channels.orders.filters.externalId", "Source id")}
          </Label>
          <Input
            id="intake-external"
            dir="ltr"
            className="w-48 font-mono"
            value={externalId}
            onChange={(event) => setExternalId(event.target.value)}
          />
        </div>

        {canRetry ? (
          <Button
            type="button"
            className="ms-auto"
            disabled={selected.length === 0 || retrying}
            onClick={() => void runRetry()}
          >
            <RefreshCw className={cn("size-4", retrying && "animate-spin")} aria-hidden="true" />
            {t("channels.orders.retry", "Retry {{count}} selected", { count: selected.length })}
          </Button>
        ) : null}
      </div>

      {retryError === null ? null : (
        <p role="alert" className="text-sm text-destructive">
          {retryError}
        </p>
      )}
      {retryResults === null ? null : (
        <div role="status" className="rounded-md border bg-muted/40 p-3 text-sm">
          <p className="mb-2 font-medium">{t("channels.orders.retryResult", "Retry accepted")}</p>
          <ul className="space-y-1">
            {retryResults.map((result) => (
              <li key={result.id} className="flex items-center gap-2">
                <span dir="ltr" className="font-mono text-xs opacity-70">
                  {result.id.slice(0, 8)}
                </span>
                <span>
                  {t(`channels.retryDisposition.${result.disposition}`, result.disposition)}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <DataTable
        columns={columns}
        query={query}
        getRowId={(row) => row.id}
        onRowClick={(row) => {
          setOpenId(row.id);
          setRawOpen(false);
        }}
        emptyMessage={t("channels.orders.empty", "No channel orders match these filters.")}
        storageKey="channels.orders.columns"
      />

      <Dialog
        open={openId !== null}
        onOpenChange={(open) => {
          if (!open) {
            setOpenId(null);
            setRawOpen(false);
          }
        }}
      >
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {t("channels.orders.detailTitle", "Intake record")}{" "}
              <span dir="ltr" className="font-mono text-sm">
                {openOrder?.increment_id ?? openOrder?.external_id ?? ""}
              </span>
            </DialogTitle>
            <DialogDescription>
              {t(
                "channels.orders.detailHint",
                "The latest attempt and the retained source revision. Neither history is published.",
              )}
            </DialogDescription>
          </DialogHeader>

          {detail.isLoading ? (
            <p className="text-sm text-muted-foreground">{t("common.loading", "Loading…")}</p>
          ) : openOrder === undefined ? (
            <p role="alert" className="text-sm text-destructive">
              {t("channels.orders.detailError", "This record could not be loaded.")}
            </p>
          ) : (
            <div className="space-y-4 text-sm">
              <dl className="grid grid-cols-[auto_1fr] gap-x-6 gap-y-2">
                <dt className="text-muted-foreground">
                  {t("channels.orders.columns.channel", "Channel")}
                </dt>
                <dd>{channelName(openOrder.channel_id)}</dd>
                <dt className="text-muted-foreground">
                  {t("channels.orders.columns.status", "Status")}
                </dt>
                <dd>
                  <Badge variant={STATUS_VARIANTS[openOrder.status]}>
                    {t(`channels.orderStatus.${openOrder.status}`, openOrder.status)}
                  </Badge>
                </dd>
                {openOrder.next_retry_at === undefined ? null : (
                  <>
                    <dt className="text-muted-foreground">
                      {t("channels.orders.nextRetry", "Next retry")}
                    </dt>
                    <dd>{when(openOrder.next_retry_at)}</dd>
                  </>
                )}
                {openOrder.imported_order_id === undefined ? null : (
                  <>
                    <dt className="text-muted-foreground">
                      {t("channels.orders.salesOrder", "Sales order")}
                    </dt>
                    <dd>
                      {onOpenSalesOrder === undefined ? (
                        <span dir="ltr" className="font-mono text-xs">
                          {openOrder.imported_order_id}
                        </span>
                      ) : (
                        <Button
                          type="button"
                          variant="link"
                          className="h-auto p-0"
                          onClick={() => onOpenSalesOrder(openOrder.imported_order_id as string)}
                        >
                          {t("channels.orders.openSalesOrder", "Open the sales order")}
                        </Button>
                      )}
                    </dd>
                  </>
                )}
              </dl>

              {attempt === undefined ? null : (
                <div className="rounded-md border p-3">
                  <h3 className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                    {t("channels.orders.lastAttempt", "Last attempt")}
                  </h3>
                  <p>
                    {t("channels.orders.attemptLine", "Attempt {{n}} · {{outcome}} · {{when}}", {
                      n: attempt.attempt_number,
                      outcome: t(`channels.attemptOutcome.${attempt.outcome}`, attempt.outcome),
                      when: when(attempt.attempted_at),
                    })}
                  </p>
                  {attempt.error_code === undefined ? null : (
                    <p className="mt-1 text-destructive">
                      {t(`channels.errorStage.${attempt.error_stage ?? "fetch"}`, "")} ·{" "}
                      <span dir="ltr" className="font-mono text-xs">
                        {attempt.error_code}
                      </span>
                    </p>
                  )}
                </div>
              )}

              {revision === undefined ? null : (
                <div className="rounded-md border p-3">
                  <h3 className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                    {t("channels.orders.sourceRevision", "Source revision")}
                  </h3>
                  <p>
                    {t("channels.orders.revisionLine", "Revision {{n}} via {{source}} · {{when}}", {
                      n: revision.revision,
                      source: t(`channels.revisionSource.${revision.source}`, revision.source),
                      when: when(revision.received_at),
                    })}
                  </p>
                  <p className="mt-1 text-xs text-muted-foreground">
                    {revision.raw_available
                      ? t("channels.orders.rawKeptUntil", "Payload kept until {{when}}", {
                          when: when(revision.retention_expires_at),
                        })
                      : t("channels.orders.rawGone", "The payload has been purged.")}
                  </p>

                  {canReadRaw && revision.raw_available ? (
                    <div className="mt-3 space-y-2">
                      <Button
                        type="button"
                        variant={rawOpen ? "outline" : "secondary"}
                        size="sm"
                        onClick={() => setRawOpen(!rawOpen)}
                      >
                        {rawOpen
                          ? t("channels.orders.hideRaw", "Hide source payload")
                          : t("channels.orders.showRaw", "Show source payload")}
                      </Button>
                      <p className="text-xs text-muted-foreground">
                        {t("channels.orders.rawAudited", "Every view of the payload is recorded.")}
                      </p>
                      {rawOpen && raw.isLoading ? (
                        <p className="text-sm text-muted-foreground">
                          {t("common.loading", "Loading…")}
                        </p>
                      ) : null}
                      {rawOpen && rawPurged ? (
                        <p role="status" className="text-sm text-muted-foreground">
                          {t("channels.orders.rawGone", "The payload has been purged.")}
                        </p>
                      ) : null}
                      {rawOpen && raw.isError && !rawPurged ? (
                        <p role="alert" className="text-sm text-destructive">
                          {t("channels.orders.rawError", "The payload could not be read.")}
                        </p>
                      ) : null}
                      {rawOpen && raw.data !== undefined ? (
                        <pre
                          dir="ltr"
                          className="max-h-72 overflow-auto rounded-md bg-muted p-3 text-xs"
                        >
                          {JSON.stringify(raw.data.raw, null, 2)}
                        </pre>
                      ) : null}
                    </div>
                  ) : null}
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setOpenId(null)}>
              {t("actions.close", "Close")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
